import { Trans, t } from "@web-monorepo/i18n/macro";
import {
  DDSButton,
  DDSDialog,
  DDSDialogBody,
  DDSDialogContent,
  DDSDialogFooter,
  DDSDialogHeader,
  DDSProvider,
  DDSTextField,
  DDSTextarea,
} from "@web-monorepo/dds";
import { IconX } from "@web-monorepo/dds-icons";
import { useSideEffectOnMount } from "@web-monorepo/hooks";
import { useRef } from "react";
import type { ToolListItem, ToolPayload } from "src/pods/toolkitData/components/EditableSetup";
import styles from "./DirectionsEditor.module.css";
import {
  useDirectionsEditor,
  type UseDirectionsEditorProps,
  type UseDirectionsEditorResult,
} from "./useDirectionsEditor.vm";

type DirectionsEditorProps = UseDirectionsEditorProps & {
  /**
   * Dependency injection for the view model — Storybook stories override this
   * with a stub `useVm` returning canned state. Production callers leave the
   * default.
   */
  di?: {
    useVm?: (props: UseDirectionsEditorProps) => UseDirectionsEditorResult;
  };
};

export default function DirectionsEditor({ di, ...props }: DirectionsEditorProps) {
  const useVm = di?.useVm ?? useDirectionsEditor;
  const {
    list,
    title,
    askForNewItem,
    canSubmit,
    setNewItemText,
    openNewItemInput,
    cancelNewItem,
    submitNewItem,
    onTitleChange,
    deleteItem,
  } = useVm(props);

  return (
    <article>
      <section>
        <DDSTextField
          data-name="directions_editor:activity_title"
          label={t`Activity name (optional)`}
          value={title}
          onChange={onTitleChange}
        />
        <ol className={styles.list}>
          {list.map((item: ToolListItem, idx: number) => (
            <li key={idx} className={styles.item}>
              <span className={styles.itemCount}>
                <Trans>{idx + 1}.</Trans>
              </span>
              <p className={styles.itemText}>{item.text}</p>
              <button
                type="button"
                data-name="directions_editor:list:ideas_close_icon"
                className={styles.deleteItemButton}
                aria-label={t`Delete item`}
                onClick={() => deleteItem(idx)}
              >
                <IconX />
              </button>
            </li>
          ))}
          <li className={styles.item}>
            <span className={styles.addItemCount}>
              <Trans>{list.length + 1}.</Trans>
            </span>
            <DDSButton
              data-name="directions_editor:render:directions_add_item"
              className={styles.addItemText}
              onClick={openNewItemInput}
              variant="ghost"
            >
              <Trans>Add item</Trans>
            </DDSButton>
          </li>
        </ol>
      </section>

      {askForNewItem && (
        <DDSProvider>
          <DDSDialog data-name="directions_editor:render:standard_modal" open>
            <DDSDialogContent data-name="directions_editor:render:standard_modal" onClose={cancelNewItem}>
              <DDSDialogHeader visuallyHideTitle title={t`Add New Item`} onClose={cancelNewItem} />
              <DDSDialogBody>
                <div className={styles.noteArea}>
                  <NewItemTextarea onChange={setNewItemText} />
                </div>
              </DDSDialogBody>
              <DDSDialogFooter>
                <DDSButton
                  variant="tertiary"
                  data-name="directions_editor:ask_for_new_item:cancel"
                  onClick={cancelNewItem}
                >
                  <Trans>Cancel</Trans>
                </DDSButton>
                <DDSButton
                  data-name="directions_editor:ask_for_new_item:add"
                  disabled={!canSubmit}
                  onClick={submitNewItem}
                >
                  <Trans>Add</Trans>
                </DDSButton>
              </DDSDialogFooter>
            </DDSDialogContent>
          </DDSDialog>
        </DDSProvider>
      )}
    </article>
  );
}

export type { ToolPayload };

// Conditionally-mounted dialog input — auto-focuses on mount so keyboard
// + screen-reader users land in the field when the dialog opens.
// DDSTextarea doesn't accept an `autoFocus` prop, so we drive focus via
// the forwarded ref (the pre-migration code used the native `autoFocus`
// attribute for the same effect).
function NewItemTextarea({ onChange }: { onChange: (value: string) => void }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useSideEffectOnMount(() => {
    ref.current?.focus();
  });
  return (
    <DDSTextarea
      ref={ref}
      data-name="directions_editor:new_item:textarea"
      label={t`Enter new item's name or description`}
      rows={3}
      onChange={onChange}
    />
  );
}
